import PropTypes, {
  array,
  arrayOf,
  bool,
  elementType,
  func,
  number,
  object,
  objectOf,
  oneOf,
  oneOfType,
  shape,
  string,
} from 'prop-types'
import { ORDER_RESULT_SORTING } from 'settings/app/constants'
import { isRequiredIf } from 'settings/propTypeDefinitions'

/**
 * default props
 * @type {{currentSort: string, is_aop: boolean, is_treatment_reason: boolean, generalTooltips: {}, singleQSRIndicator: *[], active: boolean, isBooked: boolean, searchKey: string, qsrIdentifier: *[], isBookable: boolean, linkComponent: string, showDataRows: boolean, isMobile: boolean, showDistance: boolean, open: boolean}}
 */
export const resultTileDefaultProps = {
  isMobile: false,
  locationDistance: false,
  open: false,
  active: false,
  is_treatment_reason: false,
  is_aop: false,
  generalTooltips: {},
  qsrIdentifier: [],
  currentSort: 'alphabetical',
  searchKey: '',
  singleQSRIndicator: [],
  isBooked: false,
  isBookable: false,
  showDistance: true,
  linkComponent: 'div',
  showDataRows: true,
}

/**
 * primary propTypes
 * @type {Requireable<InferProps<{currentSort: Requireable<NonNullable<InferType<Requireable<string>|(function(*, *, *): (Error|undefined))|*>>>, is_aop: Requireable<boolean>, is_treatment_reason: Requireable<boolean>, generalTooltips: ((function(*=, *=, *=, ...[*]): (*))|*), singleQSRIndicator: Requireable<(string | undefined | null)[]>, active: Requireable<boolean>, isBooked: Requireable<boolean>, handleHover: Requireable<(...args: any[]) => any>, searchKey: Requireable<string>, type: Validator<NonNullable<string>>, qsrIdentifier: Requireable<((InferPropsInner<Pick<{identifier: Requireable<string>, title: Requireable<string>}, never>> & Partial<InferPropsInner<Pick<{identifier: Requireable<string>, title: Requireable<string>}, "identifier" | "title">>>) | undefined | null)[]>, result: Requireable<InferProps<{qsrIndicators: Requireable<((InferPropsInner<Pick<{identifier: Requireable<string>, sorting: Requireable<number>, rating: Requireable<number>, tooltip: Requireable<string>, text: Requireable<string>, title: Requireable<string>, info: Requireable<string>, status: Requireable<number>}, never>> & Partial<InferPropsInner<Pick<{identifier: Requireable<string>, sorting: Requireable<number>, rating: Requireable<number>, tooltip: Requireable<string>, text: Requireable<string>, title: Requireable<string>, info: Requireable<string>, status: Requireable<number>}, "identifier" | "sorting" | "rating" | "tooltip" | "text" | "title" | "info" | "status">>>) | undefined | null)[]>, benefits: Requireable<((InferPropsInner<Pick<{moreLink: Requireable<string>, copy: Requireable<string>, headline: Requireable<string>}, never>> & Partial<InferPropsInner<Pick<{moreLink: Requireable<string>, copy: Requireable<string>, headline: Requireable<string>}, "moreLink" | "copy" | "headline">>>) | undefined | null)[]>, treatment: ((function(*=, *=, *=, ...[*]): (*))|*), distance: Requireable<number>, consultedTeam: ((function(*=, *=, *=, ...[*]): (*))|*), isAccidentInsuranceDoctor: Requireable<boolean>, hasOnlineAppointment: Requireable<boolean>, transparencyReport: Requireable<string>, qsr: Requireable<((InferPropsInner<Pick<{identifier: Requireable<string>, sorting: Requireable<number>, rating: Requireable<number>, tooltip: Requireable<string>, text: Requireable<string>, title: Requireable<string>, info: Requireable<string>, status: Requireable<number>}, never>> & Partial<InferPropsInner<Pick<{identifier: Requireable<string>, sorting: Requireable<number>, rating: Requireable<number>, tooltip: Requireable<string>, text: Requireable<string>, title: Requireable<string>, info: Requireable<string>, status: Requireable<number>}, "identifier" | "sorting" | "rating" | "tooltip" | "text" | "title" | "info" | "status">>>) | undefined | null)[]>, contribution: Requireable<((InferPropsInner<Pick<{amount: Validator<NonNullable<string>>, careLevel: Validator<NonNullable<string>>, contractId: Requireable<number>, type: Validator<NonNullable<string>>}, never>> & Partial<InferPropsInner<Pick<{amount: Validator<NonNullable<string>>, careLevel: Validator<NonNullable<string>>, contractId: Requireable<number>, type: Validator<NonNullable<string>>}, "amount" | "careLevel" | "contractId" | "type">>>) | undefined | null)[]>, iqtig: Requireable<string>, showDataRows: Requireable<boolean>, subjectArea: ((function(*=, *=, *=, ...[*]): (*))|*), id: Validator<NonNullable<NonNullable<InferType<Requireable<string>|Requireable<number>>>>>, peq: ((function(*=, *=, *=, ...[*]): (*))|*), email: Requireable<string>, cooperation: ((function(*=, *=, *=, ...[*]): (*))|*), updatedAt: Requireable<string>, locationName: Requireable<string>, address: Validator<NonNullable<InferProps<{zip: Validator<NonNullable<string>>, city: Validator<NonNullable<string>>, phone: Requireable<string>, street: Validator<NonNullable<string>>, streetAddition: Requireable<string>}>>>, docType: ((function(*=, *=, *=, ...[*]): (*))|*), coordinates: Requireable<InferProps<{lon: Requireable<number>, lat: Requireable<number>}>>, pricePerHour: Requireable<string>, services: Requireable<((InferPropsInner<Pick<{title: Requireable<string>}, never>> & Partial<InferPropsInner<Pick<{title: Requireable<string>}, "title">>>) | undefined | null)[]>, phone: Requireable<string>, byAppointmentOnly: Requireable<boolean>, openingHoursInfo: Requireable<string>, openIndicator: ((function(*=, *=, *=, ...[*]): (*))|*), isDoctorInAttendance: Requireable<boolean>, onlyPrivatePatients: ((function(*=, *=, *=, ...[*]): (*))|*), levelsGranted: Requireable<boolean>, openingHours: ((function(*=, *=, *=, ...[*]): (*))|*), coreTeam: ((function(*=, *=, *=, ...[*]): (*))|*), levels: Requireable<(string | undefined | null)[]>}>>, isBookable: Requireable<boolean>, linkComponentProps: ((function(*=, *=, *=, ...[*]): (*))|*), linkComponent: ((function(*=, *=, *=, ...[*]): (*))|*), isMobile: Requireable<boolean>, showDistance: Requireable<boolean>, setBooked: Requireable<(...args: any[]) => any>, open: Requireable<boolean>}>>}
 */
export const resultTileProps = {
  isMobile: bool,
  // not for asv teams (no detailProfil) - link to detail profil
  linkComponent: isRequiredIf(
    elementType,
    (props) =>
      'hospitals' === props.type ||
      ('doctors' === props.type && 'doctor' === props?.result?.docType)
  ),
  // not for asv teams (no detailProfil) - link props
  linkComponentProps: isRequiredIf(
    object,
    (props) =>
      'hospitals' === props.type ||
      ('doctors' === props.type && 'doctor' === props?.result?.docType)
  ),
  // accordion => flag for open/closed
  open: bool,
  // flag for hover state
  active: bool,
  // tile variants (nav stage 1)
  type: oneOf([
    'hospitals',
    'doctors',
    'midwives',
    'ambulances',
    'oncallservices', // health navigator
    'careservices',
    'carehomes',
    'caresupport',
    'dayandnightcare', // care navigator
  ]).isRequired,
  // flag for treatment reason (depends on search)
  is_treatment_reason: bool,
  // flag for ambulant operation (depends on search => 1-275)
  is_aop: bool,
  // handle bookmark state
  setBooked: func,
  // handle hover state
  handleHover: func,
  // only hospitals > tooltips
  generalTooltips: isRequiredIf(
    objectOf(string).isRequired,
    (props) => 'hospitals' === props.type
  ),
  // only hospitals > possible qsr identifiers (is required when one hospital has no qsr data and others will have these prop) // TODO. finde a better explanation
  qsrIdentifier: arrayOf(shape({ identifier: string, title: string })),
  // result sorting
  currentSort: oneOfType([
    oneOf(ORDER_RESULT_SORTING),
    // supplied prop must start with qsr_
    function (props, propName, componentName) {
      if (
        typeof props[propName] === 'string' &&
        !props[propName].startsWith('qsr_')
      ) {
        return new Error(
          `Invalid prop \`${ propName }\` supplied to \`${ componentName }\`. Validation failed.`
        )
      }
    },
  ]),
  // search query
  searchKey: string,
  // only hospitals > special cases (query: N40, C61)
  singleQSRIndicator: arrayOf(string),
  // only hospitals and doctors
  isBooked: bool,
  // switch for toggling bookmark feature
  isBookable: bool,
  // switch for toggling distance info
  showDistance: bool,
  // result specific data
  result: shape({
    // general result data (all variants will provide this props)
    id: oneOfType([string, number]).isRequired,
    locationDistance: oneOfType([string, bool]),
    locationName: string,
    address: shape({
      street: PropTypes.string.isRequired,
      zip: PropTypes.string.isRequired,
      city: PropTypes.string.isRequired,
      streetAddition: PropTypes.string,
      phone: PropTypes.string,
    }).isRequired,
    // midwives do not have geo data
    coordinates: shape({
      lat: number,
      lon: number,
    }),
    // distance (only set on location based search)
    distance: number,
    phone: string,
    email: string,

    /** ****************************************************/
    /*                Hospitals SPECIFIC DATA             */
    /** ****************************************************/
    peq: isRequiredIf(
      shape({
        general: number,
        medicalCare: number,
        organisationAndService: number,
        nursingCare: number,
      }),
      (props) => 'hospitals' === props.type
    ),
    // hospital specific data
    qsr: arrayOf(
      shape({
        identifier: string,
        title: string,
        text: string,
        rating: number,
        info: string,
        tooltip: oneOfType([string, shape({ title: string, tip: string })]),
        // indicators: arrayOf(),   // TODO BE fragen ob wir das hier noch brauchen
        status: number,
        sorting: number,
      })
    ),
    // only
    iqtig: string,

    // part of hospital data (depends on search)
    treatment: isRequiredIf(
      shape({
        department_id: string.isRequired,
        department_name: string.isRequired,
        year: string.isRequired,
        cases: number.isRequired,
        cases_ambulant: number.isRequired,
        scoring: oneOf(['LOW', 'MEDIUM', 'HIGH']), // TODO
        cases_ambulant_scoring: oneOf(['LOW', 'MEDIUM', 'HIGH']), // TODO entweder zahlen oder string (inkonsistent)
        treatment_tip: string,
        ambulant_treatment_tip: string,
      }),
      (props) => 'hospitals' === props.type
    ),

    // qsr single indicators (query => n40)
    qsrIndicators: arrayOf(
      shape({
        identifier: string,
        info: string,
        rating: oneOf([1, 2, 3]),
        sorting: number,
        status: oneOf([0, 1, 2]),
        text: string,
        title: string,
        tooltip: string,
      })
    ),

    /** ****************************************************/
    /*                 DOCTORS SPECIFIC DATA              */
    /** ****************************************************/
    subjectArea: isRequiredIf(
      string,
      (props) => 'doctors' === props.type && 'doctor' === props?.result?.docType
    ),
    cooperation: isRequiredIf(string, (props) => 'doctors' === props.type),
    docType: isRequiredIf(
      oneOf(['asvTeam', 'doctor']),
      (props) => 'doctors' === props.type
    ), // to differentiation between doctor and asv
    // asvTeam only
    coreTeam: isRequiredIf(
      arrayOf(
        shape({
          id: number.isRequired,
          name: string.isRequired,
          teamLeading: bool.isRequired,
        })
      ),
      (props) =>
        'doctors' === props.type && 'asvTeam' === props?.result?.docType
    ),
    // asvTeam only
    consultedTeam: isRequiredIf(
      arrayOf(
        shape({
          name: string.isRequired,
          teamLeading: bool.isRequired,
        })
      ),
      (props) =>
        'doctors' === props.type && 'asvTeam' === props?.result?.docType
    ),
    openingHours: isRequiredIf(
      oneOfType([array, objectOf(arrayOf(string))]),
      (props) =>
        ('doctors' === props.type && 'doctor' === props?.result?.docType) ||
        'oncallservices' === props.type
    ),
    openingHoursInfo: string,
    openIndicator: isRequiredIf(
      shape({
        state: string.isRequired,
        text: string.isRequired,
      }),
      (props) =>
        ('doctors' === props.type && 'doctor' === props?.result?.docType) ||
        'oncallservices' === props.type
    ),
    hasOnlineAppointment: bool,
    onlyPrivatePatients: isRequiredIf(
      shape({
        onlyPrivatePatients: bool,
        label: string,
        tip: string,
      }),
      (props) => 'doctors' === props.type && 'doctor' === props?.result?.docType
    ),
    byAppointmentOnly: bool,
    isAccidentInsuranceDoctor: bool,
    isDoctorInAttendance: bool,
    // aok offers for customers (depends in clientID)
    benefits: arrayOf(
      shape({
        headline: string,
        copy: string,
        moreLink: string,
      })
    ),

    /** ****************************************************/
    /*                 Midwives SPECIFIC DATA             */
    /** ****************************************************/
    services: arrayOf(shape({ title: string })),
    updatedAt: string,

    /** ****************************************************/
    /*               Ambulances SPECIFIC DATA             */
    /** ****************************************************/
    levels: arrayOf(string),
    levelsGranted: bool, // for rendering

    /** **********************************************************************/
    /*     Carehomes && Careservices && Dayandnightcare SPECIFIC DATA        */
    /** **********************************************************************/
    contribution: arrayOf(
      shape({
        type: oneOf([
          'personalContribution',
          'personalContributionShortTerm',
          'contributionDay',
          'contributionNight',
        ]).isRequired,
        amount: string,
        careLevel: string, // carehomes && careservices
        days: number, // dayandnightcare
        contractId: oneOfType([number, string]),
      })
    ),
    transparencyReport: string,
    // use for carehomes and careservices for hiding data rows
    showDataRows: bool,

    /** ****************************************************/
    /*           CareSupport  SPECIFIC DATA                */
    /** ****************************************************/
    pricePerHour: string,
    handleCostEstimate: func,
  }),
}
